import { useEffect, useState } from 'react';
import PageLayout from '../components/PageLayout';
import ImportModal from '../components/ImportModal';
import { useCompany } from '../context/CompanyContext';
import api from '../api';
import './Dashboard.css';

const EMPTY_FORM = {
  client_id: '',
  name_of_person_or_company: '',
  pan: '',
  date_of_filing: '',
  e_verify_status: 'Pending',
  fees_received: 0,
  remarks: '',
};

function ItrFiling() {
  const { selectedCompanyId, selectedCompany } = useCompany();
  const isBusinessOwner = selectedCompany?.client_mode === 'Business Owner';
  const [rows, setRows] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [toast, setToast] = useState(null);
  const [saving, setSaving] = useState(false);
  const [importOpen, setImportOpen] = useState(false);

  const refresh = () => {
    if (!selectedCompanyId) return Promise.resolve();
    return api.get('/itr-filing', { params: { company_id: selectedCompanyId } }).then((res) => setRows(res.data));
  };

  useEffect(() => {
    if (!selectedCompanyId) return;
    setLoading(true);
    Promise.all([
      refresh(),
      api.get('/clients', { params: { company_id: selectedCompanyId } }).then((res) => setClients(res.data)),
    ]).finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCompanyId]);

  const clientName = (id) => clients.find((c) => c.id === id)?.name_of_client || '—';

  const showToast = (msg) => {
    setToast(msg);
    setTimeout(() => setToast(null), 3000);
  };

  const openAdd = () => {
    setEditingId(null);
    setForm(EMPTY_FORM);
    setModalOpen(true);
  };

  const openEdit = (r) => {
    setEditingId(r.id);
    setForm({
      client_id: r.client_id || '',
      name_of_person_or_company: r.name_of_person_or_company || '',
      pan: r.pan || '',
      date_of_filing: r.date_of_filing || '',
      e_verify_status: r.e_verify_status || 'Pending',
      fees_received: r.fees_received || 0,
      remarks: r.remarks || '',
    });
    setModalOpen(true);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!selectedCompanyId) return;
    setSaving(true);
    const payload = {
      ...form,
      company_id: selectedCompanyId,
      client_id: form.client_id ? Number(form.client_id) : null,
      date_of_filing: form.date_of_filing || null,
      fees_received: Number(form.fees_received) || 0,
    };
    const request = editingId ? api.put(`/itr-filing/${editingId}`, payload) : api.post('/itr-filing', payload);
    request
      .then(() => {
        refresh();
        showToast(editingId ? 'ITR record updated ✓' : 'ITR record added ✓');
        setModalOpen(false);
      })
      .finally(() => setSaving(false));
  };

  const confirmDelete = () => {
    if (!deleteTarget) return;
    api.delete(`/itr-filing/${deleteTarget.id}`).then(() => {
      refresh();
      showToast(`${deleteTarget.name_of_person_or_company} deleted`);
      setDeleteTarget(null);
    });
  };

  return (
    <PageLayout>
      <div className="page-title-row">
        <h1 className="page-title">ITR Filing Status</h1>
      </div>

      {toast && <div className="cal-toast">{toast}</div>}

      <div className="dash-card">
        <div className="page-title-row">
          <h2 className="dash-card-title">Income Tax Return Filings</h2>
          <div className="table-actions">
            <button className="dash-btn cal-btn-grey" type="button" onClick={() => setImportOpen(true)}>
              Import
            </button>
            <button className="dash-btn dash-btn-orange" type="button" onClick={openAdd}>
              + Add New Filing
            </button>
          </div>
        </div>

        <table className="dash-table">
          <thead>
            <tr>
              {!isBusinessOwner && <th>Client</th>}
              <th>Name</th>
              <th>PAN</th>
              <th>Filing Date</th>
              <th>e-Verify</th>
              <th>Fees Received</th>
              <th>Remarks</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {!loading && rows.length === 0 && (
              <tr>
                <td className="dash-empty" colSpan={isBusinessOwner ? 7 : 8}>No ITR filing records yet.</td>
              </tr>
            )}
            {rows.map((r) => (
              <tr key={r.id}>
                {!isBusinessOwner && <td>{clientName(r.client_id)}</td>}
                <td>{r.name_of_person_or_company}</td>
                <td>{r.pan || '—'}</td>
                <td>{r.date_of_filing || '—'}</td>
                <td>
                  <span className={`dash-pill ${r.e_verify_status === 'Verified' ? 'dash-pill-green' : 'dash-pill-amber'}`}>
                    {r.e_verify_status}
                  </span>
                </td>
                <td>₹{Number(r.fees_received).toLocaleString('en-IN')}</td>
                <td className="remarks-cell" title={r.remarks || ''}>{r.remarks || '—'}</td>
                <td>
                  <div className="table-actions">
                    <button className="dash-btn cal-btn-grey" type="button" onClick={() => openEdit(r)}>
                      Edit
                    </button>
                    <button className="dash-btn-red-outline" type="button" onClick={() => setDeleteTarget(r)}>
                      Delete
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {modalOpen && (
        <div className="ncf-modal-overlay" onClick={() => setModalOpen(false)}>
          <div className="ncf-modal" onClick={(e) => e.stopPropagation()}>
            <h3 className="ncf-modal-title">{editingId ? 'Edit ITR Filing' : 'Add New ITR Filing'}</h3>
            <form onSubmit={handleSubmit}>
              {!isBusinessOwner && (
                <div className="field-group">
                  <span className="field-label">Client</span>
                  <select
                    className="field-select"
                    value={form.client_id}
                    onChange={(e) => setForm({ ...form, client_id: e.target.value })}
                  >
                    <option value="">Not linked to a client</option>
                    {clients.map((c) => (
                      <option key={c.id} value={c.id}>{c.name_of_client}</option>
                    ))}
                  </select>
                </div>
              )}
              <div className="field-group">
                <span className="field-label">Name of Person/Company *</span>
                <input
                  className="field-input"
                  required
                  value={form.name_of_person_or_company}
                  onChange={(e) => setForm({ ...form, name_of_person_or_company: e.target.value })}
                />
              </div>
              <div className="field-row">
                <div className="field-group">
                  <span className="field-label">PAN</span>
                  <input
                    className="field-input"
                    value={form.pan}
                    onChange={(e) => setForm({ ...form, pan: e.target.value.toUpperCase() })}
                    maxLength={10}
                  />
                </div>
                <div className="field-group">
                  <span className="field-label">Date of Filing</span>
                  <input
                    type="date"
                    className="field-input"
                    value={form.date_of_filing || ''}
                    onChange={(e) => setForm({ ...form, date_of_filing: e.target.value })}
                  />
                </div>
              </div>
              <div className="field-row">
                <div className="field-group">
                  <span className="field-label">e-Verify Status</span>
                  <select
                    className="field-select"
                    value={form.e_verify_status}
                    onChange={(e) => setForm({ ...form, e_verify_status: e.target.value })}
                  >
                    <option value="Pending">Pending</option>
                    <option value="Verified">Verified</option>
                  </select>
                </div>
                <div className="field-group">
                  <span className="field-label">Fees Received</span>
                  <input
                    type="number"
                    className="field-input"
                    value={form.fees_received}
                    onChange={(e) => setForm({ ...form, fees_received: e.target.value })}
                  />
                </div>
              </div>
              <div className="field-group">
                <span className="field-label">Remarks</span>
                <input
                  className="field-input"
                  value={form.remarks}
                  onChange={(e) => setForm({ ...form, remarks: e.target.value })}
                />
              </div>
              <div className="ncf-modal-actions">
                <button type="button" className="dash-btn cal-btn-grey" onClick={() => setModalOpen(false)}>
                  Cancel
                </button>
                <button type="submit" className="dash-btn dash-btn-orange" disabled={saving}>
                  {saving ? 'Saving...' : 'Save'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {deleteTarget && (
        <div className="ncf-modal-overlay" onClick={() => setDeleteTarget(null)}>
          <div className="ncf-modal" onClick={(e) => e.stopPropagation()}>
            <h3 className="ncf-modal-title">Delete ITR Filing Record</h3>
            <p style={{ fontSize: 14, color: '#374151' }}>
              Are you sure you want to delete this record for <strong>{deleteTarget.name_of_person_or_company}</strong>? This cannot be undone.
            </p>
            <div className="ncf-modal-actions">
              <button className="dash-btn cal-btn-grey" type="button" onClick={() => setDeleteTarget(null)}>
                Cancel
              </button>
              <button className="dash-btn dash-btn-orange" style={{ background: '#dc2626' }} type="button" onClick={confirmDelete}>
                Yes, Delete
              </button>
            </div>
          </div>
        </div>
      )}

      <ImportModal
        open={importOpen}
        onClose={() => setImportOpen(false)}
        module="itr"
        moduleLabel="ITR Filing Status"
        companyId={selectedCompanyId}
        onImported={refresh}
        supportsCreateNew
      />
    </PageLayout>
  );
}

export default ItrFiling;
